"use client";

import React from 'react';
import { Card, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon } from '@/components/icons';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn, formatCurrency } from '@/lib/utils';

interface AuctionFieldsProps {
  startingBid: string;
  setStartingBid: (value: string) => void;
  buyNowPrice: string;
  setBuyNowPrice: (value: string) => void;
  auctionStartTime: Date | undefined;
  setAuctionStartTime: (date: Date | undefined) => void;
  auctionEndTime: Date | undefined;
  setAuctionEndTime: (date: Date | undefined) => void;
  loading: boolean;
  handlePriceInputChange: (setter: React.Dispatch<React.SetStateAction<string>>) => (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const AuctionFields: React.FC<AuctionFieldsProps> = ({
  startingBid,
  setStartingBid,
  buyNowPrice,
  setBuyNowPrice,
  auctionStartTime,
  setAuctionStartTime,
  auctionEndTime,
  setAuctionEndTime,
  loading,
  handlePriceInputChange,
}) => {
  const parseAndFormatCurrencyInput = (value: string): string => {
    const rawValue = String(value || '').replace(/[^0-9]/g, '');
    if (!rawValue) return '';
    const numValue = parseFloat(rawValue);
    return formatCurrency(numValue);
  };

  return (
    <Card className="p-4 border">
      <CardTitle className="text-lg mb-4">Detalles de la Subasta</CardTitle>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="starting-bid">Puja Inicial ({formatCurrency(0)})</Label>
          <Input
            id="starting-bid"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={formatCurrency(100)}
            value={parseAndFormatCurrencyInput(startingBid)}
            onChange={handlePriceInputChange(setStartingBid)}
            disabled={loading}
            required
            className="tabular-nums"
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="buy-now-price">Precio de Compra ya (opcional)</Label>
          <Input
            id="buy-now-price"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={formatCurrency(5000)}
            value={parseAndFormatCurrencyInput(buyNowPrice)}
            onChange={handlePriceInputChange(setBuyNowPrice)}
            disabled={loading}
            className="tabular-nums"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="grid gap-2">
          <Label htmlFor="auction-start-time">Fecha y Hora de Inicio</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !auctionStartTime && "text-muted-foreground"
                )}
                disabled={loading}
              >
                <CalendarIcon className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                {auctionStartTime ? format(auctionStartTime, "PPP HH:mm:ss", { locale: es }) : <span>Selecciona fecha y hora</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0">
              <Calendar
                mode="single"
                selected={auctionStartTime}
                onSelect={setAuctionStartTime}
                initialFocus
              />
              <div className="p-3 border-t border-primary/30">
                <Input
                  type="time"
                  value={auctionStartTime ? format(auctionStartTime, "HH:mm") : ''}
                  onChange={(e) => {
                    const [hours, minutes] = e.target.value.split(':').map(Number);
                    const newDate = auctionStartTime ? new Date(auctionStartTime) : new Date();
                    newDate.setHours(hours, minutes, 0, 0);
                    setAuctionStartTime(newDate);
                  }}
                  disabled={loading}
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="auction-end-time">Fecha y Hora de Fin</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !auctionEndTime && "text-muted-foreground"
                )}
                disabled={loading}
              >
                <CalendarIcon className="mr-2 h-3 w-3 sm:h-4 sm:w-4" />
                {auctionEndTime ? format(auctionEndTime, "PPP HH:mm:ss", { locale: es }) : <span>Selecciona fecha y hora</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0">
              <Calendar
                mode="single"
                selected={auctionEndTime}
                onSelect={setAuctionEndTime}
                initialFocus
              />
              <div className="p-3 border-t border-primary/30">
                <Input
                  type="time"
                  value={auctionEndTime ? format(auctionEndTime, "HH:mm") : ''}
                  onChange={(e) => {
                    const [hours, minutes] = e.target.value.split(':').map(Number);
                    const newDate = auctionEndTime ? new Date(auctionEndTime) : new Date();
                    newDate.setHours(hours, minutes, 59, 999);
                    setAuctionEndTime(newDate);
                  }}
                  disabled={loading}
                />
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </Card>
  );
};

export default AuctionFields;